import { addDays, civilDate, isoWeekday, zonedToUtc } from './time';
import type { CompanyRegistration } from './countries';
import type { DeclaredFact } from './scope';
import type { ImplementationStore, PreparationEntry } from '../store';

/**
 * Frontière de service de la prise de rendez-vous (POC §9A.1 : « mockable service boundaries »).
 * L'écran ne connaît que cette interface. Le POC fournit un service simulé, en mémoire. L'API v1
 * le remplacera par les commandes `BookImplementationInterview` et la requête des créneaux, une fois
 * leurs contrats écrits (plan §6, §13). Aucun agenda externe, aucun stockage du navigateur.
 */

/** Paramètres du rendez-vous ; titre et description sont des textes d'écran, traduits (messages). */
export interface InterviewEvent {
  duration_minutes: number;
  /** Fuseau de l'organisateur : les créneaux sont définis dans ce fuseau. */
  host_time_zone: string;
}

export interface Slot {
  /** Instant UTC ISO 8601. */
  start: string;
  end: string;
}

export interface BookingRequest {
  slot: Slot;
  time_zone: string;
  /** Pays ISO 3166-1 et identifiant du registre de ce pays ; SIREN et SIRET seulement en France (spec §9A.3). */
  company: { legal_name: string; country: string; registration?: CompanyRegistration; siren?: string; siret?: string; website?: string };
  primary_contact: { name: string; role?: string; email: string };
  /** Langue choisie par le client : celle de l'entretien et des messages. */
  locale: string;
  declared_context: { current_systems: string[]; primary_objective?: string };
  declared_facts: DeclaredFact[];
  /** Le client souhaite qu'un consultant humain assiste à l'entretien mené par l'IA. */
  consultant_requested?: boolean;
}

/** Forme de `ImplementationEngagement` (spec §9A.3), telle que rendue après la réservation. */
export interface ImplementationEngagement {
  engagement_id: string;
  company: BookingRequest['company'];
  primary_contact: BookingRequest['primary_contact'];
  interview: { scheduled_at: string; ends_at: string; timezone: string; locale: string; status: 'scheduled' };
  declared_context: BookingRequest['declared_context'];
  pre_discovery: { status: 'not_started'; facts: DeclaredFact[] };
  /** Vrai tant que la réservation vient du service simulé : affiché, jamais caché. */
  simulated: boolean;
}

export interface BookingService {
  event(): InterviewEvent;
  /** Créneaux libres dont le début tombe entre `from` et `to` (dates civiles du fuseau hôte, incluses). */
  availability(from: string, to: string): Promise<Slot[]>;
  book(request: BookingRequest): Promise<ImplementationEngagement>;
  /** Aide facultative à la préparation (POC §9A.7) : fichier, système, information, ou étape passée. */
  addPreparation(engagementId: string, entry: Omit<PreparationEntry, 'at'>): Promise<void>;
}

export class SlotUnavailableError extends Error {
  constructor() {
    super('SLOT_UNAVAILABLE');
    this.name = 'SlotUnavailableError';
  }
}

export interface MockBookingOptions {
  now?: () => Date;
  /** Heures de début dans le fuseau hôte, `HH:MM`. */
  start_times?: readonly string[];
  /** Délai minimal avant un rendez-vous. */
  min_notice_hours?: number;
  /** Fenêtre de réservation ouverte, en jours. */
  horizon_days?: number;
  /** Instants déjà pris (UTC ISO), pour simuler un agenda occupé. */
  taken?: readonly string[];
  latency_ms?: number;
  /** Magasin de démonstration où la réservation est rangée, pour le back-office. */
  store?: ImplementationStore;
}

const DEFAULT_EVENT: InterviewEvent = { duration_minutes: 60, host_time_zone: 'Europe/Paris' };

function hexId(): string {
  const bytes = new Uint8Array(12);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

const wait = (ms: number): Promise<void> => (ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve());

/** Service simulé : jours ouvrés, heures fixes du fuseau hôte, délai de prévenance, créneaux pris. */
export function createMockBookingService(options: MockBookingOptions = {}): BookingService {
  const now = options.now ?? (() => new Date());
  const startTimes = options.start_times ?? ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'];
  const minNotice = (options.min_notice_hours ?? 24) * 3_600_000;
  const horizon = options.horizon_days ?? 45;
  const latency = options.latency_ms ?? 0;
  const event = DEFAULT_EVENT;
  const taken = new Set(options.taken ?? []);

  function slotsOf(date: string): Slot[] {
    if (isoWeekday(date) > 5) return [];
    return startTimes.map((time) => {
      const [hour, minute] = time.split(':').map(Number) as [number, number];
      const start = zonedToUtc(date, hour, minute, event.host_time_zone);
      return { start: start.toISOString(), end: new Date(start.getTime() + event.duration_minutes * 60_000).toISOString() };
    });
  }

  function open(slot: Slot): boolean {
    const start = Date.parse(slot.start);
    const current = now().getTime();
    return !taken.has(slot.start) && start - current >= minNotice && start - current <= horizon * 86_400_000;
  }

  return {
    event: () => event,
    async availability(from, to) {
      await wait(latency);
      const out: Slot[] = [];
      for (let date = from; date <= to; date = addDays(date, 1)) for (const slot of slotsOf(date)) if (open(slot)) out.push(slot);
      return out;
    },
    async book(request) {
      await wait(latency);
      const known = slotsOf(civilDate(new Date(request.slot.start), event.host_time_zone)).some((slot) => slot.start === request.slot.start);
      if (!known || !open(request.slot)) throw new SlotUnavailableError();
      taken.add(request.slot.start);
      const engagement: ImplementationEngagement = {
        engagement_id: hexId(),
        company: request.company,
        primary_contact: request.primary_contact,
        interview: { scheduled_at: request.slot.start, ends_at: request.slot.end, timezone: request.time_zone, locale: request.locale, status: 'scheduled' },
        declared_context: request.declared_context,
        pre_discovery: { status: 'not_started', facts: request.declared_facts },
        simulated: true,
      };
      options.store?.put({ engagement, status: 'scheduled', consultant_requested: request.consultant_requested === true, preparation: [], booked_at: now().toISOString() });
      return engagement;
    },
    async addPreparation(engagementId, entry) {
      options.store?.update(engagementId, (record) => ({ ...record, preparation: [...record.preparation, { ...entry, at: now().toISOString() }] }));
    },
  };
}

/* Validation des coordonnées : côté écran pour guider, à refaire côté serveur (deny par défaut). */

export function emailIssue(value: string): 'missing' | 'format' | null {
  if (value.trim() === '') return 'missing';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? null : 'format';
}

/** Site web facultatif ; `exemple.fr` est accepté et complété en `https://exemple.fr`. */
export function normalizeWebsite(value: string): { url?: string; issue: 'format' | null } {
  const trimmed = value.trim();
  if (trimmed === '') return { issue: null };
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!url.hostname.includes('.')) return { issue: 'format' };
    return { url: url.toString(), issue: null };
  } catch {
    return { issue: 'format' };
  }
}
